import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Play, 
  ArrowRight, 
  CheckCircle, 
  Users, 
  Star, 
  BookOpen, 
  Zap,
  Globe,
  BarChart3
} from "lucide-react";
import Navbar from "./Navbar";

const features = [
  {
    icon: BookOpen,
    title: "Structured Learning",
    description: "Progressive lessons from Hangul basics to advanced conversations",
    color: "from-red-500 to-orange-500"
  },
  {
    icon: Users,
    title: "Interactive Practice",
    description: "Engage with quizzes, flashcards, and pronunciation exercises",
    color: "from-orange-500 to-amber-500"
  },
  {
    icon: Globe,
    title: "Cultural Context",
    description: "Understand Korean culture alongside the language",
    color: "from-amber-500 to-yellow-500"
  },
  {
    icon: BarChart3,
    title: "Progress Tracking",
    description: "Monitor your learning journey with detailed analytics",
    color: "from-orange-500 to-red-500"
  }
];

const stats = [
  { number: "500+", label: "Active Learners", icon: Users },
  { number: "4.8/5", label: "User Rating", icon: Star },
  { number: "20+", label: "Lessons Available", icon: BookOpen },
  { number: "24/7", label: "Free Access", icon: Zap }
];

const testimonials = [
  {
    name: "Sarah Kim",
    role: "University Student",
    content: "As someone reconnecting with my Korean heritage, Korlish has been perfect. The cultural context really helps me understand the language better.",
    rating: 5
  },
  {
    name: "Michael Rodriguez",
    role: "K-Pop Fan",
    content: "I love how Korlish explains the cultural background behind phrases. It's helping me understand K-dramas and K-pop lyrics much better!",
    rating: 5
  },
  {
    name: "Emma Thompson",
    role: "Travel Enthusiast",
    content: "Planning my first trip to Korea and Korlish is giving me the confidence to try basic conversations. The lessons are well-structured and engaging.",
    rating: 4
  }
];

function LandingPage() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [animatedStats, setAnimatedStats] = useState([0, 0, 0, 0]);

  useEffect(() => {
    setIsVisible(true);
    
    // Animate stats numbers
    const animateStats = () => {
      const targets = [500, 4.8, 20, 24];
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;
      
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        const easeOut = 1 - Math.pow(1 - progress, 3);
        
        setAnimatedStats(targets.map(target => Math.floor(target * easeOut)));
        
        if (step >= steps) {
          clearInterval(timer);
          setAnimatedStats(targets);
        }
      }, stepDuration);
    };

    const timer = setTimeout(animateStats, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Floating Korean Characters */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            {[
              { char: '한', left: '15%', top: '20%' },
              { char: '글', left: '85%', top: '15%' },
              { char: '학', left: '20%', top: '75%' },
              { char: '습', left: '80%', top: '80%' },
              { char: '어', left: '10%', top: '50%' },
              { char: '화', left: '90%', top: '50%' }
            ].map((item, index) => {
              const colors = ['text-red-500', 'text-orange-500', 'text-amber-600', 'text-red-600'];
              const opacities = ['opacity-50', 'opacity-40', 'opacity-45', 'opacity-35'];
              const colorClass = colors[index % colors.length];
              const opacityClass = opacities[index % opacities.length];
              
              return (
                <div
                  key={index}
                  className={`absolute text-6xl sm:text-8xl font-black ${colorClass} ${opacityClass} animate-float drop-shadow-lg`}
                  style={{
                    left: item.left,
                    top: item.top,
                    animationDelay: `${index * 0.3}s`,
                    animationDuration: `${5 + Math.random() * 1}s`,
                    textShadow: '3px 3px 6px rgba(0,0,0,0.4)',
                    filter: 'blur(0.5px)',
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.8s ease-out'
                  }}
                >
                  {item.char}
                </div>
              );
            })}
          </div>

          {/* Traditional Korean Pattern Background */}
          <div className="absolute inset-0 opacity-10 z-0">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23dc2626' fill-opacity='0.15'%3E%3Cpath d='M60 30c16.569 0 30 13.431 30 30s-13.431 30-30 30-30-13.431-30-30 13.431-30 30-30zm0 10c-11.046 0-20 8.954-20 20s8.954 20 20 20 20-8.954 20-20-8.954-20-20-20z'/%3E%3Cpath d='M30 30c16.569 0 30 13.431 30 30s-13.431 30-30 30S0 76.569 0 60s13.431-30 30-30zm0 10c-11.046 0-20 8.954-20 20s8.954 20 20 20 20-8.954 20-20-8.954-20-20-20z'/%3E%3Cpath d='M90 30c16.569 0 30 13.431 30 30s-13.431 30-30 30-30-13.431-30-30 13.431-30 30-30zm0 10c-11.046 0-20 8.954-20 20s8.954 20 20 20 20-8.954 20-20-8.954-20-20-20z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '120px 120px'
            }}></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center lg:pt-32">
            <div className={`mx-auto max-w-5xl transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Korean Greeting with Traditional Style */}
              <div className="mb-8">
                <div className="inline-block bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-3 rounded-full text-xl font-bold shadow-2xl border-4 border-white">
                  안녕하세요! (Hello!)
                </div>
              </div>

              {/* Logo with Traditional Korean Design */}
              <div className="flex justify-center mb-12">
                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
                  <div className="absolute -inset-2 bg-white rounded-full shadow-2xl"></div>
        <img
          src="/korlish.png"
          alt="Korlish Logo"
                    className="h-32 w-32 rounded-full shadow-2xl ring-4 ring-red-200 relative z-10"
                  />
                  <div className="absolute -top-4 -right-4 bg-red-600 text-white text-lg font-bold px-3 py-2 rounded-full animate-bounce shadow-lg">
                    🇰🇷
                  </div>
                  <div className="absolute -bottom-4 -left-4 bg-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full animate-pulse">
                    한글
                  </div>
                </div>
              </div>

              {/* Main Heading with Traditional Korean Typography */}
              <h1 className="text-7xl md:text-8xl font-black text-gray-900 mb-8 leading-tight">
                Master Korean with{" "}
                <span className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 bg-clip-text text-transparent drop-shadow-lg">
                  Korlish
                </span>
              </h1>

              {/* Korean Subtitle with Traditional Style */}
              <div className="mb-8 bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border-2 border-red-200">
                <p className="text-4xl text-red-700 font-black mb-3">한국어를 배워보세요!</p>
                <p className="text-xl text-gray-600 font-semibold">(Learn Korean!)</p>
              </div>

              {/* Subtitle */}
              <p className="text-2xl text-gray-800 mb-12 max-w-4xl mx-auto leading-relaxed font-medium">
                Your comprehensive Korean learning platform. From Hangul basics to conversational fluency,
                I make Korean language learning engaging, effective, and fun.
              </p>

              {/* CTA Buttons with Traditional Korean Style */}
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center mb-20">
                <button
                  onClick={() => navigate("/login")}
                  className="group bg-gradient-to-r from-red-600 to-orange-600 text-white px-16 py-8 rounded-3xl text-2xl font-black hover:from-red-700 hover:to-orange-700 transform hover:scale-110 transition-all duration-300 shadow-2xl hover:shadow-red-500/50 flex items-center space-x-4 border-4 border-white"
                >
                  <Play className="w-8 h-8" />
                  <span>Start Learning Free</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </button>
        <button
          onClick={() => navigate("/lessons")}
                  className="bg-white/95 backdrop-blur-sm text-gray-800 px-16 py-8 rounded-3xl text-2xl font-black border-4 border-red-300 hover:border-red-500 hover:bg-red-50 transition-all duration-300 shadow-2xl hover:shadow-xl"
        >
                  Browse Lessons
        </button>
              </div>

              {/* Trust Indicators with Traditional Korean Colors */}
              <div className="flex items-center justify-center space-x-16 text-xl text-gray-700">
                <div className="flex items-center space-x-3 bg-white/80 px-6 py-3 rounded-full shadow-lg">
                  <CheckCircle className="w-8 h-8 text-red-600" />
                  <span className="font-bold">Free Forever</span>
                </div>
                <div className="flex items-center space-x-3 bg-white/80 px-6 py-3 rounded-full shadow-lg">
                  <CheckCircle className="w-8 h-8 text-red-600" />
                  <span className="font-bold">No Ads</span>
                </div>
                <div className="flex items-center space-x-3 bg-white/80 px-6 py-3 rounded-full shadow-lg">
                  <CheckCircle className="w-8 h-8 text-red-600" />
                  <span className="font-bold">Mobile Friendly</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Korean Learning Journey Section */}
        <div className="py-24 bg-gradient-to-br from-amber-100 via-orange-50 to-red-50 relative">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23dc2626' fill-opacity='0.1'%3E%3Cpath d='M40 20c11.046 0 20 8.954 20 20s-8.954 20-20 20-20-8.954-20-20 8.954-20 20-20zm0 8c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '80px 80px'
            }}></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-6xl font-black text-gray-900 mb-6">
                Your Korean Learning Journey
              </h2>
              <p className="text-2xl text-gray-700 max-w-4xl mx-auto font-semibold">
                From beginner to fluent - I'll guide you every step of the way
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
              {stats.map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="mb-8 flex justify-center">
                    <div className="p-8 bg-gradient-to-br from-red-200 to-orange-200 rounded-full group-hover:scale-125 transition-transform duration-500 shadow-2xl border-4 border-white">
                      <stat.icon className="w-12 h-12 text-red-700" />
                    </div>
                  </div>
                  <div className="text-5xl font-black text-red-700 mb-4">
                    {index === 0 && `${animatedStats[0]}+`}
                    {index === 1 && `${animatedStats[1]}/5`}
                    {index === 2 && `${animatedStats[2]}+`}
                    {index === 3 && `${animatedStats[3]}/7`}
                  </div>
                  <div className="text-gray-800 font-bold text-xl">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Features Section with Dramatic Korean Theme */}
        <div className="py-24 bg-gradient-to-br from-red-100 via-orange-100 to-amber-100 relative">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23dc2626' fill-opacity='0.1'%3E%3Ccircle cx='50' cy='50' r='20'/%3E%3Ccircle cx='20' cy='20' r='8'/%3E%3Ccircle cx='80' cy='20' r='8'/%3E%3Ccircle cx='20' cy='80' r='8'/%3E%3Ccircle cx='80' cy='80' r='8'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '100px 100px'
            }}></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-24">
              <h2 className="text-6xl font-black text-gray-900 mb-8">
                왜 Korlish를 선택해야 할까요?
              </h2>
              <p className="text-3xl text-gray-700 max-w-5xl mx-auto leading-relaxed font-bold">
                (Why Choose Korlish?)
              </p>
              <p className="text-2xl text-gray-600 max-w-5xl mx-auto mt-6 font-semibold">
                I've designed every aspect of your Korean learning journey to be effective and enjoyable
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group relative bg-white rounded-3xl p-10 hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 border-4 border-red-200 hover:border-red-400 shadow-xl"
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <div className="relative z-10">
                    <div className="mb-8 flex justify-center">
                      <div className={`p-8 bg-gradient-to-br ${feature.color} rounded-full group-hover:scale-125 transition-transform duration-500 shadow-2xl border-4 border-white`}>
                        <feature.icon className="w-12 h-12 text-white" />
                      </div>
                    </div>
                    <h3 className="text-3xl font-black text-gray-900 mb-6 group-hover:text-red-700 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-lg group-hover:text-gray-800 transition-colors duration-300 font-medium">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials Section with Dramatic Korean Style */}
        <div className="py-24 bg-gradient-to-br from-white to-amber-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-24">
              <h2 className="text-6xl font-black text-gray-900 mb-8">
                학습자들의 이야기
              </h2>
              <p className="text-3xl text-gray-700 max-w-5xl mx-auto font-bold">
                (What Our Learners Say)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-gradient-to-br from-red-100 to-orange-100 rounded-3xl p-10 hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-4 border-red-200 shadow-xl flex flex-col h-full">
                  <div className="flex items-center mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-800 mb-8 leading-relaxed text-xl font-medium flex-1">
                    "{testimonial.content}"
                  </p>
                  <div className="mt-auto">
                    <div className="font-black text-gray-900 text-xl">{testimonial.name}</div>
                    <div className="text-gray-700 font-semibold text-lg">{testimonial.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final CTA Section with Dramatic Korean Colors */}
        <div className="py-24 bg-gradient-to-r from-red-600 via-orange-600 to-amber-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M60 30c16.569 0 30 13.431 30 30s-13.431 30-30 30-30-13.431-30-30 13.431-30 30-30zm0 10c-11.046 0-20 8.954-20 20s8.954 20 20 20 20-8.954 20-20-8.954-20-20-20z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '120px 120px'
            }}></div>
          </div>
          <div className="relative max-w-6xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-6xl font-black text-white mb-8">
              한국어 학습을 시작할 준비가 되셨나요?
            </h2>
            <p className="text-3xl text-white/95 mb-6 font-bold">
              (Ready to Start Your Korean Journey?)
            </p>
            <p className="text-2xl text-white/90 mb-16 leading-relaxed font-semibold">
              Join our growing community of Korean learners.
              Start your free learning journey today!
            </p>
            <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
              <button
            onClick={() => navigate("/login")}
                className="group bg-white text-red-700 px-16 py-8 rounded-3xl text-3xl font-black hover:bg-gray-100 transform hover:scale-110 transition-all duration-300 shadow-2xl flex items-center space-x-4 border-4 border-white"
              >
                <span>Begin Learning Now</span>
                <ArrowRight className="w-8 h-8 group-hover:translate-x-3 transition-transform" />
              </button>
              <button
                onClick={() => navigate("/about")}
                className="bg-white/20 backdrop-blur-sm text-white px-16 py-8 rounded-3xl text-3xl font-black border-4 border-white/50 hover:bg-white/30 transition-all duration-300 shadow-2xl"
              >
                Learn More About Me
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LandingPage;